import { motion } from "framer-motion";

// The four active verbs of emotional literacy — one per book in the series.
const VERBS = [
  { verb: "Loving",     book: "Book One · Love Well" },
  { verb: "Noticing",   book: "Book Two · Being Thankful" },
  { verb: "Discerning", book: "Book Three · Listening Inside" },
  { verb: "Speaking",   book: "Book Four · Being Small and Strong" },
];

/**
 * FourVerbs — the pedagogical spine of the series.
 *
 * Props:
 *   variant = "full"    → eyebrow + H1 + paragraph + four-column verbs (Parents page)
 *   variant = "compact" → eyebrow + H1 + paragraph only          (The Book page)
 */
export default function FourVerbs({ variant = "full" }) {
  return (
    <section data-testid="four-verbs" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-6 items-end mb-12 md:mb-14">
          <div className="md:col-span-8">
            <div className="italic-serif text-sm text-charcoal/60 mb-3">
              The pedagogical spine of the series
            </div>
            <h2 className="serif text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.02] tracking-tight text-espresso">
              Four verbs. <span className="italic text-[#4A7C7B]">One growing library.</span>
            </h2>
          </div>
        </div>

        <p className="max-w-3xl text-charcoal/90 leading-relaxed text-base md:text-lg">
          The Love Well series teaches four active verbs of emotional literacy — loving, noticing, discerning, and speaking. Each book models one verb, delivered with a portable tool a child can carry with them: a phrase for parents who don't show up, a thank-you rock for noticing the good, the &ldquo;uh-oh feeling&rdquo; for discerning what's off, the small strong voice for speaking up. A gentle library that grows with a child, from noticing what hurts to trusting one's own voice.
        </p>

        {variant === "full" && (
          <div className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8 md:gap-x-6 border-t border-black/10 pt-10 md:pt-14">
            {VERBS.map((v, i) => (
              <motion.div
                key={v.verb}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`verb-${v.verb.toLowerCase()}`}
                className="flex flex-col"
              >
                <div className="mono text-[10px] tracking-[0.28em] uppercase text-charcoal/55">
                  0{i + 1}
                </div>
                <div className="italic-serif text-3xl md:text-4xl text-espresso mt-3">
                  {v.verb}
                </div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#4A7C7B] mt-4 leading-relaxed">
                  {v.book}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
